/**
 * pastoralPriorityQueue.js
 * Cross-source pastoral priority aggregation.
 *
 * Aggregates from:
 *   - District state (insights + pressure)
 *   - Relationship memory (entity signal histories)
 *   - Workflow followups (overdue/stagnant)
 *
 * Outputs a ranked list of pastoral attention items,
 * each phrased in pastoral language (never analytics).
 */

import { getDistrictState }         from "../kevia-data/districtStateEngine";
import { getAllEntityHistories }     from "../kevia-relationships/relationalMemoryEngine";
import { computeFollowupItems }      from "../kevia-workflows/followupEngine";

const RISKY_TONES = new Set(["followup_urgent", "sensitive"]);

/**
 * @typedef {{
 *   priority:  "high" | "medium" | "low",
 *   label:     string,
 *   source:    "district" | "relationship" | "workflow",
 *   entityName?: string,
 *   score:     number,
 * }} PriorityItem
 */

/**
 * Build a unified pastoral priority queue from all available data sources.
 * Pure + fast (all in-memory / localStorage reads).
 *
 * @returns {{ items: PriorityItem[], hasHighPriority: boolean, compact: string | null }}
 */
export function buildPastoralPriorityQueue() {
  const items = [];

  // ── District-level insights ───────────────────────────────────────────────
  const districtState = getDistrictState();
  if (districtState?.insights?.length) {
    for (const ins of districtState.insights.slice(0, 3)) {
      items.push({
        priority:  ins.severity === "high" ? "high" : "medium",
        label:     ins.insight,
        source:    "district",
        score:     ins.severity === "high" ? 0.90 : 0.60,
      });
    }
  }

  // ── Relationship memory — at-risk entities ────────────────────────────────
  const histories = getAllEntityHistories();
  for (const record of histories.slice(0, 10)) {
    const recent   = record.signals[0];
    if (!recent) continue;
    if (!RISKY_TONES.has(recent.tone)) continue;

    const days     = recent.visitDays;
    const daysLabel = days != null ? ` (${days}d sin visita)` : "";
    const label    =
      recent.tone === "sensitive"
        ? `${record.name} — situación delicada activa${daysLabel}.`
        : `${record.name} — convendría retomar el contacto${daysLabel}.`;

    items.push({
      priority:   "high",
      label,
      source:     "relationship",
      entityName: record.name,
      score:      recent.tone === "sensitive" ? 0.85 : 0.75,
    });
  }

  // ── Workflow followups ────────────────────────────────────────────────────
  const followups = computeFollowupItems();
  for (const f of followups.slice(0, 3)) {
    items.push({
      priority:   f.kind === "overdue" ? "high" : "medium",
      label:      f.label,
      source:     "workflow",
      entityName: f.entityName ?? undefined,
      score:      f.score,
    });
  }

  // Sort by score descending, deduplicate by label prefix
  const sorted = items
    .sort((a, b) => b.score - a.score)
    .filter((item, idx, arr) =>
      arr.findIndex((x) => x.label.slice(0, 30) === item.label.slice(0, 30)) === idx
    );

  const hasHighPriority = sorted.some((i) => i.priority === "high");

  // Compact representation — top 2 items, pastoral language
  const compact = sorted.length > 0
    ? sorted.slice(0, 2).map((i) => i.label).join(" ")
    : null;

  return { items: sorted, hasHighPriority, compact };
}
